import Vue from 'vue'
import decode from 'jwt-decode'
import { getUser, getRefreshToken, getAccessToken, setUser, setAccessToken, setRefreshToken, clearCredentials } from '@/utils/cookie'
import { EventBus } from '@/router/bus'
import i18n from '@/i18n'
import securityStore from '@/store/security-store.js'
import router from '@/router/index.js'

// Claims
const CLAIMS = [
  'Claim_SystemUser_Review', // 0
  'Claim_SystemUser_Create', // 1
  'Claim_SystemUser_Revoke', // 2
  'Claim_User_Review', // 3
  'Claim_User_Review_All', // 4
  'Claim_User_Create', // 5
  'Claim_User_Update', // 6
  'Claim_PaymentOrder_Generate', // 7
  'Claim_Issue_Review', // 8
  'Claim_Issue_Update', // 9
  'Claim_Subsidy_Report' // 10
]

const ADMIN_CLAIM = 'Claim_Admin'

const AUTHORIZATION = 'Authorization'

// Public methods

export function getClaims () {
  return CLAIMS
}

export function isUserActive () {
  return isAuth() && accessTokenValid()
}

// TODO Check this if it's valid
export function isAuth () {
  return !!getUser()
}

export function accessTokenValid () {
  var token = getAccessToken()
  return token && !isTokenExpired(token)
}

export function refreshTokenValid () {
  var token = getRefreshToken()
  return token && !isTokenExpired(token)
}

export function setCredentials (user, accessToken, refreshToken) {
  setUser(user)
  setAccessToken(accessToken)
  setRefreshToken(refreshToken)

  securityStore.dispatch('setIsAuth')
  const decodedToken = decode(accessToken)
  securityStore.dispatch('setClaims', decodedToken.security_claims)
  securityStore.dispatch('setExpirationDate', decodedToken.exp)
  securityStore.dispatch('setUserType', decodedToken.budget_user_direct_indirect_type)
  // let authorizationKey = Sha256(user, Vue.$config.secret_key)
  // Vue.http.headers.common['From'] = authorizationKey.toString()
  // console.log(Vue.http.headers)
}

export function logout () {
  router.push('/')
  securityStore.dispatch('unsetIsAuth')
  clearCredentials()
}

export function refreshToken (to, from, next) {
  var refreshTokenUrl = Vue.$apiConfig.loginRefresh
  const isDevelopmentEnvironment = process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'pmiproduction'

  if (isDevelopmentEnvironment) { console.log('Access token expired - sending refresh access token request from router') }

  Vue.http.headers.common[AUTHORIZATION] = 'Bearer ' + getRefreshToken()

  Vue.http.get(refreshTokenUrl)
    .then(response => {
      if (isDevelopmentEnvironment) { console.log('Access token obtained successfully - redirecting to ' + to.path) }
      setAccessToken(response.data.payload.accessToken)
      Vue.http.headers.common[AUTHORIZATION] = 'Bearer ' + getAccessToken()
      // refreshReportingHierarchy()
      next({
        path: to.path
      })
    }, error => {
      if (isDevelopmentEnvironment) { console.log(error) }

      if (error.status === 0) {
        EventBus.$emit('alert', {
          variant: 'error',
          message: i18n.t('apiStatusCodes.0'),
          errors: []
        })
      }

      next({
        path: '/'
      })
    })
}

export function hasPermissionChangeAuthority (claims) {
  // Allow always for admin
  if (hasAdminClaim()) { return true }

  // Allow operators to change all users - except admins
  if (hasSystemUserCreateClaim()) {
    if (claims.constructor === Array) {
      if (claims.indexOf(ADMIN_CLAIM) >= 0) {
        return false
      }
    } else if (claims.localeCompare(ADMIN_CLAIM) === 0) {
      return false
    }

    return true
  }

  return false
}

// Private methods

function hasClaim (claim) {
  const claims = securityStore.getters.claims
  if (claims.constructor === Array) {
    return claims.indexOf(claim) >= 0 || claims.indexOf(ADMIN_CLAIM) >= 0
  }

  return claims.localeCompare(claim) === 0 || claims.localeCompare(ADMIN_CLAIM) === 0
}

function getTokenExpirationDate (encodedToken) {
  var token = decode(encodedToken)
  if (!token.exp) { return null }

  var date = new Date(0)
  date.setUTCSeconds(token.exp)

  return date
}

function isTokenExpired (token) {
  var expirationDate = getTokenExpirationDate(token)
  return expirationDate < new Date()
}

// check claim functions

export function getUserName () {
  return getTokenUserName()
}

export function hasAdminClaim () {
  return hasClaim(ADMIN_CLAIM)
}

export function hasSystemUserReviewClaim () {
  return hasClaim(CLAIMS[0])
}

export function hasSystemUserCreateClaim () {
  return hasClaim(CLAIMS[1])
}

export function hasSystemUserRevokeClaim () {
  return hasClaim(CLAIMS[2])
}

export function hasUserReviewClaim () {
  return hasClaim(CLAIMS[3])
}

export function hasUserReviewAllClaim () {
  return hasClaim(CLAIMS[4])
}

export function hasUserCreateClaim () {
  return hasClaim(CLAIMS[5])
}

export function hasUserUpdateClaim () {
  return hasClaim(CLAIMS[6])
}

export function hasPaymentOrderGenerateClaim () {
  return hasClaim(CLAIMS[7])
}

export function hasIssueReviewClaim () {
  return hasClaim(CLAIMS[8])
}

export function hasIssueUpdateClaim () {
  return hasClaim(CLAIMS[9])
}

export function hasSubsidyReportClaim () {
  return hasClaim(CLAIMS[10])
}

function getTokenUserName () {
  var token = getAccessToken()
  var decodedToken = decode(token)

  return decodedToken.user_name
}
